import React, { useEffect, useState } from 'react';
import { Card, Avatar, Empty, Spin, message, Tag, Button, Modal } from 'antd';
import { MessageOutlined, RobotOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { chatApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import { useTabStore } from '../store/tabStore';
import './SessionManagement.css';

interface SavedSession {
  id: number;
  session_id: string;
  saved_name: string;
  summary: string;
  employee_id: number | null;
  employee_name: string | null;
  employee_avatar: string | null;
  created_at: string;
  updated_at: string;
  total_tokens: number;
  message_count: number;
}

const SessionManagement: React.FC = () => {
  const navigate = useNavigate();
  const { addTab } = useTabStore();
  const [sessions, setSessions] = useState<SavedSession[]>([]);
  const [loading, setLoading] = useState(true);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data: any = await chatApi.listSavedSessions();
      setSessions(data);
    } catch (error) {
      message.error('加载会话列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleSessionClick = (session: SavedSession) => {
    if (session.employee_id) {
      const path = `/employees/${session.employee_id}/chat?session=${session.session_id}`;
      addTab({
        key: `chat:${session.employee_name}`,
        path,
        label: session.employee_name || '对话',
        closable: true,
      });
      navigate(path);
    }
  };

  const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    Modal.confirm({
      title: '删除会话',
      content: '确定要删除这个会话吗？',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await chatApi.deleteSession(sessionId);
          message.success('删除成功');
          loadSessions();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  return (
    <div className="apple-page">
      <div className="apple-page-header" style={{ minHeight: 32 }}>
        <h1 className="apple-page-title">会话管理</h1>
      </div>
      <p className="apple-page-subtitle">管理已保存的会话，快速访问历史对话。</p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin size="large" />
        </div>
      ) : sessions.length === 0 ? (
        <Empty description="暂无已保存的会话" style={{ marginTop: 60 }} />
      ) : (
        <div className="session-grid">
          {sessions.map((session) => (
            <Card
              key={session.session_id}
              className="session-card"
            >
              <div className="session-card-title">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{session.saved_name}</div>
                </div>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  className="session-card-delete"
                  onClick={(e) => handleDelete(e, session.session_id)}
                />
              </div>

              <div className="session-chat-preview">
                <div className="chat-row user-row">
                  <Avatar
                    size={28}
                    src={session.employee_avatar ? resolveUrl(session.employee_avatar) : undefined}
                    icon={!session.employee_avatar && <RobotOutlined />}
                  />
                  <div className="chat-name">{session.employee_name}</div>
                </div>
                <div className="chat-row ai-row">
                  <div className="chat-bubble ai-bubble">{session.summary?.length > 250 ? session.summary.slice(0, 250) + '…' : session.summary}</div>
                </div>
              </div>

              <div className="session-card-footer">
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span><MessageOutlined /> {session.message_count} 条</span>
                  <span>• {new Date(session.updated_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <Button 
                  type="text" 
                  size="small" 
                  className="session-continue-btn"
                  onClick={() => handleSessionClick(session)}
                >
                  继续沟通
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionManagement;
